"use client";

import { c as _c } from "react/compiler-runtime";
import { CarouselTabItem, CarouselTabs } from "@/components/common/carousel-tabs";
import { Card, CardContent, CardHeader, CardTitle, _CardDescription } from "@/components/rac/card";
import { ButtonLink } from "@/components/ui/button-link";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { COMPANY_INFO } from "@/info/company";
import { SELLER_PRICING } from "@/lib/constants/seller-pricing";
import { AnimatePresence, m } from "framer-motion";
import { FileText, UserCheck, Users } from "lucide-react";
import { useRouter } from "next/navigation";
import * as React from "react";
const tabs: CarouselTabItem[] = [{
  id: "buyers",
  label: <>
				<span className="hidden sm:inline">Buyers</span>
				<span className="sm:hidden">Buyers</span>
			</>,
  icon: <Users className="w-4 h-4" />,
  content: () => <div className="bg-green-50 border border-green-200 rounded-xl p-8">
				<div className="text-center mb-6">
					<h2 className="text-2xl font-bold text-secondary mb-2">Buyers</h2>
					<div className="text-3xl font-bold text-green-600 mb-2">
						<span className="line-through text-gray-400 text-2xl">0.5%</span> ZERO{" "}
					</div>
					<div className="text-gray-600 text-lg">if you finalize our properties</div>
					<p className="text-sm text-gray-500 mt-2">Consultancy Fee (Postpaid)</p>
				</div>

				<div className="space-y-4 mb-6">
					<h3 className="text-lg font-semibold text-gray-800">Services Included:</h3>
					<ul className="space-y-3">
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>
								<strong>Free Legal opinion</strong> on the property (worth ₹10,000)
							</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Free sale agreement drafting, documentation and assistance till registration (worth ₹20,000)</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Free home loan assistance (from the bank of your choice)</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Free Assisted Buyer inspections</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Direct negotiations with the seller, if requested</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Only verified properties</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Prior seller confirmation before every visit</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Complete transparency in the entire process</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Tripartite agreement to safeguard buyer interests post negotiation</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Real time notifications/updates on the app</span>
						</li>
					</ul>
				</div>

				<div className="text-center">
					<ButtonLink href="/bengaluru" className="w-full max-w-md">
						Start Property Search
					</ButtonLink>
				</div>
			</div>
}, {
  id: "sellers",
  label: <>
				<span className="hidden sm:inline">Sellers</span>
				<span className="sm:hidden">Sellers</span>
			</>,
  icon: <UserCheck className="w-4 h-4" />,
  content: () => <div className="bg-red-50 border border-red-200 rounded-xl p-8">
				<div className="text-center mb-6">
					<h2 className="text-2xl font-bold text-red-600 mb-2">Sellers</h2>
					<div className="text-3xl font-bold text-gray-800 mb-2">
						<span className="line-through text-gray-400 text-2xl">2.18%</span>
						{SELLER_PRICING.feePercentage}%
					</div>
					<div className="text-lg text-gray-600">of total sale value (inclusive of GST)</div>
					<p className="text-lg text-gray-500 mt-2">
						subject to a minimum of {SELLER_PRICING.minimumFee}, and a maximum of {SELLER_PRICING.maximumFee} (inclusive
						of GST)
					</p>
				</div>

				<div className="mt-2 mb-6 @container">
					<strong>Fee Examples:</strong>
					<div className="grid grid-cols-1 @sm:grid-cols-3 @lg:grid-cols-4 gap-3">
						{SELLER_PRICING.examples.map((example, index) => <div key={index} className="p-3 rounded-lg border border-primary-200">
								<div className="text-sm font-medium text-gray-600">
									Sale @ <span className="text-lg font-bold text-gray-900">{example.salePriceLabel}</span>
								</div>
								<div className="text-sm text-primary-600 font-medium mt-1">Fee: {example.feeLabel}</div>
							</div>)}
					</div>
				</div>

				<div className="space-y-4 mb-6">
					<h3 className="text-lg font-semibold text-gray-800">Services Included:</h3>
					<ul className="space-y-3">
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Free Price Analysis of the property (worth ₹5,000), along with correction</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Free Marketing at prominent classifieds, till it is sold</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Handling of all responses on behalf of the seller</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Only genuine buyer visits, after perusing all property details, vaastu etc</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Prior approval before every buyer visit - through call/mail/sms/notification</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Complete transparency in the entire process</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Negotiations on behalf of the seller</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Direct negotiations, if requested</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Tripartite agreement to safeguard seller interests post negotiation</span>
						</li>
						<li className="flex items-start">
							<span className="text-green-500 mr-2 mt-1">✓</span>
							<span>Real time notifications/updates on the app</span>
						</li>
					</ul>
				</div>

				<div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-6">
					<p className="text-cyan-700 font-semibold text-center">
						Payment only after the deal is closed, only if the buyer is brought by {COMPANY_INFO.name}
					</p>
				</div>

				<div className="text-center">
					<ButtonLink href="/sell" variant="accent" className="w-full max-w-md">
						Request a Listing
					</ButtonLink>
				</div>
			</div>
}, {
  id: "services",
  label: <>
				<span className="hidden sm:inline">Services</span>
				<span className="sm:hidden">Services</span>
			</>,
  icon: <FileText className="w-4 h-4" />,
  content: () => <div className="space-y-6 sm:space-y-8 pb-8">
				{/* Legal Services Overview */}
				<div className="bg-primary-50 border border-primary-200 text-gray-800 rounded-xl p-6 sm:p-8">
					<div className="text-center mb-6">
						<h2 className="text-2xl font-bold mb-4 text-primary-700">Legal Services</h2>
						<p className="text-lg text-gray-600 mb-4">
							Comprehensive legal services to protect your property investments
						</p>
					</div>

					{/* Main Service Categories Grid */}
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
						{/* Complete Documentation Package */}
						<Card className={"transition-all flex flex-col"}>
							<CardHeader>
								<CardTitle className="text-lg font-semibold">📋 Complete Documentation</CardTitle>
								<_CardDescription>
									<span className="text-xl font-bold text-primary">₹29,999</span>
									<span className={"text-muted-foreground mr-2"}>+ GST</span>
									<span className={"line-through text-muted-foreground mr-2"}> </span>
								</_CardDescription>
							</CardHeader>
							<CardContent className="flex-1">
								<ul className="text-sm text-muted-foreground list-disc pl-4">
									<li>Legal Opinion</li>
									<li>Sale Agreement</li>
									<li>Sale Deed drafting</li>
									<li>Registration assistance</li>
									<li>Bank loan support</li>
								</ul>
							</CardContent>
						</Card>

						{/* Legal Opinion */}
						<Card className={"transition-all flex flex-col"}>
							<CardHeader>
								<CardTitle className="text-lg font-semibold">⚖️ Legal Opinion</CardTitle>
								<_CardDescription>
									<span className="text-xl font-bold text-primary">₹12,000</span>
									<span className={"text-muted-foreground mr-2"}>+ GST</span>
									<span className={"line-through text-muted-foreground mr-2"}> </span>
								</_CardDescription>
							</CardHeader>
							<CardContent className="flex-1">
								<ul className="text-sm text-muted-foreground list-disc pl-4">
									<li>Document verification</li>
									<li>Legal compliance check</li>
									<li>Risk assessment</li>
									<li>Expert consultation</li>
								</ul>
							</CardContent>
						</Card>

						{/* Property Deeds */}
						<Card className={"transition-all flex flex-col"}>
							<CardHeader>
								<CardTitle className="text-lg font-semibold">📜 Property Deeds</CardTitle>
								<_CardDescription>
									<span className="text-xl font-bold text-primary">₹10,000</span>
									<span className={"text-muted-foreground mr-2"}>Onwards</span>
									<span className={"line-through text-muted-foreground mr-2"}> </span>
								</_CardDescription>
							</CardHeader>
							<CardContent className="flex-1">
								<ul className="text-sm text-muted-foreground list-disc pl-4">
									<li>Sale Deed</li>
									<li>Gift Deed</li>
									<li>Release Deed</li>
									<li>Power of Attorney</li>
								</ul>
							</CardContent>
						</Card>

						{/* Name Transfer Services */}
						<Card className={"transition-all flex flex-col"}>
							<CardHeader>
								<CardTitle className="text-lg font-semibold">🔄 Name Transfers</CardTitle>
								<_CardDescription>
									<span className="text-xl font-bold text-primary">₹6,000</span>
									<span className={"text-muted-foreground mr-2"}>Onwards</span>
									<span className={"line-through text-muted-foreground mr-2"}> </span>
								</_CardDescription>
							</CardHeader>
							<CardContent className="flex-1">
								<ul className="text-sm text-muted-foreground list-disc pl-4">
									<li>Khata Transfer</li>
									<li>Electricity Meter</li>
									<li>Water Meter</li>
									<li>Tax receipts</li>
								</ul>
							</CardContent>
						</Card>

						{/* Mortgage & Loan Services */}
						<Card className={"transition-all flex flex-col"}>
							<CardHeader>
								<CardTitle className="text-lg font-semibold">🏦 Mortgage Services</CardTitle>
								<_CardDescription>
									<span className="text-xl font-bold text-primary">₹12,000</span>
									<span className={"text-muted-foreground mr-2"}>+ GST</span>
									<span className={"line-through text-muted-foreground mr-2"}> </span>
								</_CardDescription>
							</CardHeader>
							<CardContent className="flex-1">
								<ul className="text-sm text-muted-foreground list-disc pl-4">
									<li>Mortgage Deed</li>
									<li>Reconveyance Deed</li>
									<li>Loan documentation</li>
									<li>Legal assistance</li>
								</ul>
							</CardContent>
						</Card>

						{/* Certificates & Records */}
						<Card className={"transition-all flex flex-col"}>
							<CardHeader>
								<CardTitle className="text-lg font-semibold">📋 Certificates</CardTitle>
								<_CardDescription>
									<span className="text-xl font-bold text-primary">₹1,199</span>
									<span className={"line-through text-muted-foreground mr-2"}> </span>
								</_CardDescription>
							</CardHeader>
							<CardContent className="flex-1">
								<ul className="text-sm text-muted-foreground list-disc pl-4">
									<li>Encumbrance Certificate</li>
									<li>Certified Copy</li>
									<li>Property records</li>
									<li>Official documents</li>
								</ul>
							</CardContent>
						</Card>
					</div>

					{/* Direct Documentation Special Pricing */}
					<div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6 mb-6">
						<div className="text-center mb-4">
							<h3 className="text-xl font-bold text-orange-700 mb-2">🏠 Direct Property Documentation</h3>
							<p className="text-orange-600 text-sm">Special pricing for direct buyer-seller transactions</p>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="bg-white rounded-lg p-4 border border-orange-100">
								<div className="text-center">
									<div className="text-lg font-bold text-orange-700 mb-1">For Indian Residents</div>
									<div className="text-2xl font-bold text-orange-600">₹25,000</div>
									<div className="text-xs text-orange-600 mt-2">Documentation charges only</div>
								</div>
							</div>

							<div className="bg-white rounded-lg p-4 border border-orange-100">
								<div className="text-center">
									<div className="text-lg font-bold text-orange-700 mb-1">With NRI Party</div>
									<div className="text-2xl font-bold text-orange-600">₹30,000</div>
									<div className="text-xs text-orange-600 mt-2">includes special NRI services</div>
								</div>
							</div>
						</div>

						<div className="mt-4 text-center">
							<div className="text-sm text-orange-700 font-medium">
								💡 Includes legal opinion, agreements, and registration assistance
							</div>
						</div>
					</div>

					<div className="text-center">
						<ButtonLink href="/services" className="bg-primary-600 text-white hover:bg-primary-700 px-8 py-3">
							Select Services & Get Quote
						</ButtonLink>
					</div>
				</div>

				{/* Additional Info */}
				<div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 text-center">
					<div className="text-sm text-gray-600 mb-4">
						GST applicable as per government regulations. Prices may vary based on property location and complexity.
					</div>
					<div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
						<ButtonLink href={`tel:${COMPANY_INFO.contact.phone.primary}`} variant="outline" className="border-gray-300 text-sm px-4 py-2 w-full sm:w-auto">
							📞 Call Expert
						</ButtonLink>
						<ButtonLink href="/services" className="bg-primary-600 text-white hover:bg-primary-700 text-sm px-4 py-2 w-full sm:w-auto">
							Browse All Services
						</ButtonLink>
					</div>
				</div>
			</div>
}];
export default function PricingPage(t0) {
  const $ = _c(27);
  const {
    relationship
  } = t0;
  const router = useRouter();
  const [shouldAnimate, setShouldAnimate] = React.useState(false);
  let t1;
  if ($[0] !== router) {
    t1 = function (key) {
      router.push(`/pricing/${key.toString()}`);
    };
    $[0] = router;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleTabChange = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      threshold: 0.1,
      triggerOnce: true
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    ref: contentRef,
    isInView: contentInView
  } = useScrollAnimation(t2);
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const timer = setTimeout(() => setShouldAnimate(true), 100);
      return () => clearTimeout(timer);
    };
    t4 = [];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  React.useEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] !== contentInView) {
    t5 = () => {
      if (contentInView) {
        setShouldAnimate(true);
      }
    };
    t6 = [contentInView];
    $[5] = contentInView;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  React.useEffect(t5, t6);
  let t7;
  if ($[8] !== relationship || $[9] !== shouldAnimate) {
    t7 = tabs.map(t8 => {
      const {
        id,
        content: ContentComponent,
        ...rest
      } = t8;
      let renderedContent = null;
      if (ContentComponent && typeof ContentComponent === "function" && id === relationship) {
        const componentJSX = ContentComponent();
        renderedContent = <m.div initial={{
          opacity: 1,
          y: 30
        }} animate={shouldAnimate ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 30
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="w-full">{componentJSX}</m.div>;
      }
      return {
        content: renderedContent,
        id,
        ...rest
      };
    });
    $[8] = relationship;
    $[9] = shouldAnimate;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const tabsListed = t7 as CarouselTabItem[];
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <m.div className="text-center mb-4 sm:mb-8" initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1,
      ease: "linear"
    }}><h1 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-4">Pricing</h1></m.div>;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== handleTabChange || $[13] !== relationship || $[14] !== tabsListed) {
    t9 = <AnimatePresence mode="wait"><div className="w-full space-y-2 sm:space-y-4"><CarouselTabs tabs={tabsListed} onSelectionChange={handleTabChange} selectedKey={relationship} defaultSelectedKey="buyers" variant="pills" size="md" className="mb-2 sm:mb-6" /></div></AnimatePresence>;
    $[12] = handleTabChange;
    $[13] = relationship;
    $[14] = tabsListed;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== contentRef || $[17] !== t9) {
    t10 = <div ref={contentRef} className="w-full">{t9}</div>;
    $[16] = contentRef;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <h3 className="text-2xl font-semibold mb-4 text-primary-800">Ready to Get Started?</h3>;
    t12 = <p className="text-primary-700 mb-6 max-w-2xl mx-auto">Contact us today to discuss your real estate needs. Our team is ready to help you buy or sell your property with complete transparency and professional service.</p>;
    $[19] = t11;
    $[20] = t12;
  } else {
    t11 = $[19];
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ButtonLink href="tel:+91-95-9003-9003" variant="default" size="lg">Call +91-95-9003-9003</ButtonLink>;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== relationship) {
    t14 = <div className="mt-6 sm:mt-12 text-center"><div className="bg-primary-50 border border-primary-200 rounded-lg p-8">{t11}{t12}<div className="flex flex-wrap justify-center gap-4">{t13}{relationship === "sellers" ? <ButtonLink href="/sell" variant="outline" size="lg">Post Property</ButtonLink> : <ButtonLink href="/bengaluru" variant="outline" size="lg">Start Property Search</ButtonLink>}</div></div></div>;
    $[22] = relationship;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t10 || $[25] !== t14) {
    t15 = <div className="container mx-auto py-8 sm:py-12 px-4 pb-12"><div className="max-w-6xl mx-auto">{t8}{t10}{t14}</div></div>;
    $[24] = t10;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  return t15;
}